import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserCog } from "lucide-react";

/**
 * Sticky red banner shown at the top of the app whenever the current session
 * is a superadmin impersonating another user. Provides a one-click "Exit
 * Impersonation" that swaps the session back to the original admin.
 */
export default function ImpersonationBanner() {
  const { user, refreshMe } = useAuth();

  if (!user?.impersonating) return null;

  const stop = async () => {
    try {
      await api.post("/superadmin/stop-impersonating");
      toast.success("Returned to your admin session");
      await refreshMe();
      // Reload so any cached page state (contacts, etc.) refetches under admin identity.
      window.location.href = "/superadmin";
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 bg-[var(--peak-accent)] text-white flex items-center justify-between px-6 py-2.5 border-b-2 border-black"
    >
      <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-wider">
        <UserCog className="w-4 h-4" />
        <span>
          IMPERSONATING <span className="font-mono normal-case tracking-normal">{user.email}</span>{" "}
          <span className="opacity-75">· signed in as {user.impersonated_by_email}</span>
        </span>
      </div>
      <button
        data-testid="exit-impersonation-btn"
        onClick={stop}
        className="bg-black text-white px-3 py-1 text-xs font-bold uppercase tracking-wider hover:bg-white hover:text-[var(--peak-accent)] transition-colors"
      >
        Exit Impersonation
      </button>
    </div>
  );
}
